import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiFilm, FiRefreshCw, FiArrowRight } from 'react-icons/fi';
import { useRecentTrailers } from '../hooks/useMovies';
import TrailerCard from './TrailerCard';
import InfoIcon from './InfoIcon';
import Modal from './Modal';
import { isYouTubeUrl, getYouTubeEmbedUrl } from '../utils/videoUtils';

const TrailerSpotlight: React.FC = () => {
  const { trailers, loading, error, refresh } = useRecentTrailers(10);
  const carouselRef = useRef<HTMLDivElement>(null);
  const [isHovered, setIsHovered] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeVideoUrl, setActiveVideoUrl] = useState<string | null>(null);

  const scroll = (direction: 'left' | 'right') => {
    if (carouselRef.current) {
      const { clientWidth } = carouselRef.current;
      const scrollAmount = direction === 'left' ? -clientWidth : clientWidth;
      carouselRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  const handlePlayClick = (videoUrl: string) => {
    setActiveVideoUrl(videoUrl);
    setIsModalOpen(true);
  };

  if (loading) {
    return (
      <div className="container-custom py-8 animate-fade-in">
        <div className="flex items-center gap-3 mb-6">
          <FiFilm className="w-6 h-6 text-accent-teal" />
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-secondary-dark mb-0">
            Latest Trailers
          </h2>
          <InfoIcon tooltipText="The most recently added movie trailers" />
        </div>
        <div className="flex items-center justify-center h-64">
          <div className="relative w-16 h-16">
            <div className="absolute inset-0 border-4 border-primary/20 rounded-full animate-spin" />
            <div className="absolute inset-0 border-t-4 border-primary rounded-full" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container-custom py-8 animate-fade-in">
        <div className="flex items-center gap-3 mb-6">
          <FiFilm className="w-6 h-6 text-accent-teal" />
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-secondary-dark mb-0">
            Latest Trailers
          </h2>
          <InfoIcon tooltipText="The most recently added movie trailers" />
        </div>
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <p className="text-secondary-dark mb-4">
            Oops! We couldn't load the trailers at the moment.
          </p>
          <button
            onClick={refresh}
            className="inline-flex items-center space-x-2 btn-primary"
          >
            <FiRefreshCw className="w-5 h-5" />
            <span>Try Again</span>
          </button>
        </div>
      </div>
    );
  }

  if (!trailers.length) {
    return (
      <div className="container-custom py-8 animate-fade-in">
        <div className="flex items-center gap-3 mb-6">
          <FiFilm className="w-6 h-6 text-accent-teal" />
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-secondary-dark mb-0">
            Latest Trailers
          </h2>
          <InfoIcon tooltipText="The most recently added movie trailers" />
        </div>
        <div className="bg-background-light border border-primary/10 rounded-xl p-6 text-center">
          <p className="text-secondary-dark">No trailers available yet.</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div
        className="container-custom py-8 animate-fade-in"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Section header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <FiFilm className="w-6 h-6 text-accent-teal" />
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-secondary-dark mb-0">
              Latest Trailers
            </h2>
            <InfoIcon tooltipText="The most recently added movie trailers" />
          </div>
          <Link
            to="/trailers"
            className="hidden sm:flex items-center gap-1 text-sm font-medium text-primary hover:text-primary-dark transition-colors duration-200"
          >
            See all trailers
            <FiArrowRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="relative">
          {/* Mobile Grid Layout */}
          <div className="md:hidden grid grid-cols-1 gap-4">
            {trailers.map((trailer) => (
              <div key={trailer._id} className="animate-slide-up">
                <TrailerCard trailer={trailer} onPlayClick={handlePlayClick} />
              </div>
            ))}
          </div>

          {/* Desktop Carousel */}
          <div className="hidden md:block">
            <div
              ref={carouselRef}
              className="flex space-x-4 overflow-x-auto scrollbar-thin scrollbar-thumb-primary/20 scrollbar-track-transparent pb-4"
              style={{ scrollbarWidth: 'thin' }}
            >
              {trailers.map((trailer) => (
                <div key={trailer._id} className="w-72 flex-shrink-0 animate-slide-up">
                  <TrailerCard trailer={trailer} onPlayClick={handlePlayClick} />
                </div>
              ))}
            </div>
          </div>

          {/* Navigation Arrows */}
          {trailers.length > 4 && (
            <>
              <button
                className={`hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 -ml-4 items-center justify-center w-12 h-12 rounded-full bg-white shadow-lg text-secondary-dark hover:text-primary transition-all duration-200 ${
                  isHovered ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'
                }`}
                onClick={() => scroll('left')}
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
              </button>
              <button
                className={`hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 -mr-4 items-center justify-center w-12 h-12 rounded-full bg-white shadow-lg text-secondary-dark hover:text-primary transition-all duration-200 ${
                  isHovered ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-4'
                }`}
                onClick={() => scroll('right')}
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>
            </>
          )}
        </div>

        {/* Mobile "See all" link */}
        <div className="sm:hidden mt-4 text-center">
          <Link
            to="/trailers"
            className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:text-primary-dark transition-colors duration-200"
          >
            See all trailers
            <FiArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>

      {/* Video Modal */}
      {activeVideoUrl && isYouTubeUrl(activeVideoUrl) && (
        <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
          <iframe
            src={getYouTubeEmbedUrl(activeVideoUrl) + '?autoplay=1'}
            className="absolute inset-0 w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </Modal>
      )}
    </>
  );
};

export default TrailerSpotlight;
